import AddIcon from '@mui/icons-material/Add';
import { Stack, Typography } from '@mui/material';
import { FormDialogContext } from '@react-admin/ra-form-layout';
import { useCallback } from 'react';
import { Labeled, required, SelectInput, useNotify, useRefresh, useTranslate } from 'react-admin';

import { GetInstrumentOneResponse } from '@Common/types/bo/instruments';
import {
  PostScheduleInstrumentsEditEndpoint,
  ZodScheduleInstrumentsType,
} from '@Common/types/bo/instruments_scheduled_edits';

import {
  ActionModalFormWithToolbar,
  CreateInDialogButton,
  DialogToolbar,
  SaveButton,
} from '@sh4res/bo-common/components';
import { DateTimeInput } from '@sh4res/bo-common/fields';
import { getErrorMessage } from '@sh4res/bo-common/utils';

const scheduledEditTypeChoices = ZodScheduleInstrumentsType.options.map((type) => ({
  id: type,
  name: type,
}));

type ScheduledEditFormValues = Pick<
  PostScheduleInstrumentsEditEndpoint['Client']['RequestBody'],
  'type' | 'scheduled_at'
>;

type InstrumentScheduledEditCreateButtonProps = {
  instrument: GetInstrumentOneResponse;
  'data-testid'?: string;
};

export const InstrumentScheduledEditCreateButton = ({
  instrument,
  'data-testid': dataTestId,
}: InstrumentScheduledEditCreateButtonProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleTransform = useCallback(
    (
      data: ScheduledEditFormValues,
    ): PostScheduleInstrumentsEditEndpoint['Client']['RequestBody'] => ({
      instrument_id: instrument.id,
      type: data.type,
      scheduled_at: data.scheduled_at,
    }),
    [instrument.id],
  );

  const handleSuccess = useCallback(() => {
    notify('resources.bo_common.instruments/scheduled_edits.create.success', { type: 'success' });
    refresh();
  }, [notify, refresh]);

  const handleError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.instruments/scheduled_edits.create.failure', {
        type: 'error',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  return (
    <CreateInDialogButton
      testId={dataTestId}
      resource="instruments/scheduled_edits"
      title="resources.bo_common.instruments/scheduled_edits.create.title"
      maxWidth="sm"
      fullWidth
      icon={<AddIcon />}
      record={{ instrument_id: instrument.id }}
      transform={handleTransform}
      mutationOptions={{ onSuccess: handleSuccess, onError: handleError }}
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: { fontWeight: 'bold', textTransform: 'capitalize' },
        label: translate('resources.bo_common.instruments/scheduled_edits.create.button'),
      }}>
      <ActionModalFormWithToolbar
        toolbar={
          <DialogToolbar
            formDialogContext={FormDialogContext}
            button={
              <SaveButton
                color="primary"
                permissions={{ resource: 'instruments/scheduled_edits', action: 'create' }}
                testId="BoExternalInstrumentScheduledEditsCreate_SaveButton"
              />
            }
          />
        }>
        <Stack spacing={2} sx={{ pt: 1, width: '100%' }}>
          <Labeled label="resources.bo_common.instruments/scheduled_edits.fields.instrument_id">
            <Typography>{instrument.id}</Typography>
          </Labeled>
          <SelectInput
            source="type"
            choices={scheduledEditTypeChoices}
            optionText={(choice) =>
              translate(`resources.bo_common.instruments/scheduled_edits.types.${choice.id}`)
            }
            validate={required()}
            fullWidth
            data-testid="BoExternalInstrumentScheduledEditsCreate_TypeInput"
          />
          <DateTimeInput
            source="scheduled_at"
            label="resources.bo_common.instruments/scheduled_edits.fields.scheduled_at"
            validate={required()}
            fullWidth
            data-testid="BoExternalInstrumentScheduledEditsCreate_ScheduledAtInput"
          />
        </Stack>
      </ActionModalFormWithToolbar>
    </CreateInDialogButton>
  );
};
